/**
 * Secure Shield Component
 * Manages the secure shield protection plan checkbox and drawer interactions.
 * On PDP: toggles checkbox state and drawer button visibility only.
 * On Cart/Mini-cart: additionally calls the Cart API to add/remove the shield
 * line item and morphs the section to reflect the updated cart state.
 */

class SecureShieldComponent extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  checkbox = null;

  /** @type {string | null} */
  drawerId = null;

  /** @type {boolean} */
  #isCartContext = false;

  /** @type {boolean} */
  #busy = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #syncTimeout = null;

  // -------------------------------------------------------------------------
  // Cart-context DOM getters (values come from data attributes set by Liquid)
  // -------------------------------------------------------------------------

  /** @returns {string} */
  get #parentKey() {
    return this.dataset.parentKey || '';
  }

  /** @returns {string} */
  get #parentVariant() {
    return this.dataset.parentVariant || '';
  }

  /** @returns {string} */
  get #shieldVariant() {
    const input = this.querySelector('[name="secure_shield_variant"]');
    return input instanceof HTMLInputElement ? input.value : '';
  }

  /** @returns {string} */
  get #sectionId() {
    const el = this.closest('[data-section-id]');
    return el ? (el.getAttribute('data-section-id') || 'main-cart') : 'main-cart';
  }

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  connectedCallback() {
    const checkboxElement = this.querySelector('.secure-shield-block__checkbox');
    this.checkbox = checkboxElement instanceof HTMLInputElement ? checkboxElement : null;

    if (!this.checkbox) return;

    this.drawerId = this.dataset.drawerId || null;
    this.#isCartContext = Boolean(this.dataset.parentKey);

    this.checkbox.addEventListener('change', this.#handleCheckboxChange);

    this.#initializeDrawerButton();
    this.#updateDrawerButtonVisibility();

    if (this.#isCartContext) {
      this.#initCartContext();
    }
  }

  disconnectedCallback() {
    if (this.checkbox) {
      this.checkbox.removeEventListener('change', this.#handleCheckboxChange);
    }
    if (this.#isCartContext && this.#onCartUpdated) {
      document.removeEventListener('cart:updated', this.#onCartUpdated);
      document.removeEventListener('cart:update', this.#onCartUpdated);
      document.removeEventListener('cart:refresh', this.#onCartUpdated);
    }
    if (this.#syncTimeout) {
      clearTimeout(this.#syncTimeout);
      this.#syncTimeout = null;
    }
  }

  // -------------------------------------------------------------------------
  // Cart context initialisation
  // -------------------------------------------------------------------------

  /** @type {((event: Event) => void) | null} */
  #onCartUpdated = null;

  #initCartContext() {
    // @ts-ignore – Horizon theme globals
    this.#morphSection = window.morphSection || (window.Theme && window.Theme.morphSection);
    // @ts-ignore
    this.#CartUpdateEvent = window.CartUpdateEvent || (window.Theme?.events?.CartUpdateEvent);
    // @ts-ignore
    this.#ThemeEvents = window.ThemeEvents || (window.Theme?.events);

    this.#onCartUpdated = (/** @type {Event} */ event) => {
      const customEvent = /** @type {CustomEvent} */ (event);
      const source = customEvent.detail?.data?.source || customEvent.detail?.source;
      if (source === 'secure-shield') return;
      this.#debouncedSyncShieldQuantity();
    };

    document.addEventListener('cart:updated', this.#onCartUpdated);
    document.addEventListener('cart:update', this.#onCartUpdated);
    document.addEventListener('cart:refresh', this.#onCartUpdated);
  }

  /** @type {Function | null} */
  #morphSection = null;
  /** @type {Function | null} */
  #CartUpdateEvent = null;
  /** @type {Record<string, any> | null} */
  #ThemeEvents = null;

  // -------------------------------------------------------------------------
  // Drawer helpers (shared PDP + Cart)
  // -------------------------------------------------------------------------

  /**
   * Gets the drawer component element
   * @returns {HTMLElement | null}
   */
  #getDrawerComponent() {
    if (!this.drawerId) return null;
    const drawerElement = document.getElementById(this.drawerId);
    return drawerElement instanceof HTMLElement ? drawerElement : null;
  }

  /**
   * Handles checkbox change event
   * @param {Event} event
   */
  #handleCheckboxChange = (event) => {
    this.#updateDrawerButtonVisibility();
    
    if (!this.#isCartContext || !this.checkbox) return;
    
    if (this.#busy) {
      this.checkbox.checked = !this.checkbox.checked;
      return;
    }
    

    if (this.checkbox.checked) {
      this.#addSecureShield();
    } else {
      this.#removeSecureShield();
    }
  };

  #updateDrawerButtonVisibility() {
    if (!this.checkbox) return;

    const drawerComponent = this.#getDrawerComponent();
    if (!drawerComponent) return;

    const drawerButton = drawerComponent.querySelector('[data-secure-shield-add-btn]');
    if (!(drawerButton instanceof HTMLElement)) return;

    drawerButton.style.display = this.checkbox.checked ? 'none' : '';
  }

  #initializeDrawerButton() {
    const drawerComponent = this.#getDrawerComponent();
    if (!drawerComponent) return;

    const observer = new MutationObserver(() => {
      const drawerButton = drawerComponent.querySelector('[data-secure-shield-add-btn]');
      if (drawerButton instanceof HTMLElement && !drawerButton.dataset.handlerAttached) {
        drawerButton.addEventListener('click', this.#handleDrawerButtonClick);
        drawerButton.dataset.handlerAttached = 'true';
        this.#updateDrawerButtonVisibility();
      }
    });

    observer.observe(drawerComponent, { childList: true, subtree: true });

    const existingDrawerButton = drawerComponent.querySelector('[data-secure-shield-add-btn]');
    if (existingDrawerButton instanceof HTMLElement && !existingDrawerButton.dataset.handlerAttached) {
      existingDrawerButton.addEventListener('click', this.#handleDrawerButtonClick);
      existingDrawerButton.dataset.handlerAttached = 'true';
      this.#updateDrawerButtonVisibility();
    }
  }

  /**
   * Handles drawer button click - checks the checkbox and closes the drawer
   * @param {Event} event
   */
  #handleDrawerButtonClick = (event) => {
    event.preventDefault();

    if (this.checkbox && !this.checkbox.checked) {
      this.checkbox.checked = true;
      const changeEvent = new Event('change', { bubbles: true });
      this.checkbox.dispatchEvent(changeEvent);
    }

    const drawerComponent = this.#getDrawerComponent();
    if (drawerComponent && 'closeDialog' in drawerComponent && typeof drawerComponent.closeDialog === 'function') {
      drawerComponent.closeDialog();
    }
  };

  // -------------------------------------------------------------------------
  // Cart API — Add shield (checkbox checked)
  // -------------------------------------------------------------------------

  async #addSecureShield() {
    if (this.#busy) return;
    this.#busy = true;
    this.#setCheckboxDisabled(true);
    this.#setCartItemsLoading(true);

    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      // The DOM row identifies *which* cart line the user toggled, but its data-key
      // was rendered by Liquid at page load. CartTransform / automatic discounts can
      // rotate line keys on every cart mutation, so we always reconcile against the
      // live /cart.js payload to obtain the fresh server-side key.
      const parentRow = this.closest('.bv-cart-item');
      if (!(parentRow instanceof HTMLElement)) {
        console.error('[Secure Shield] Parent cart row not found in DOM');
        this.#revertCheckbox(false);
        return;
      }

      const domParentKey = parentRow.dataset.key || '';
      const parentVariantId = Number(this.#parentVariant);
      /** @param {any} i */
      const isParentLine = (i) => !i.parent_relationship;

      const liveItems = Array.isArray(cart.items) ? cart.items : [];
      const parentItem =
        liveItems.find(/** @param {any} i */ (i) => i.key === domParentKey && isParentLine(i)) ||
        liveItems.find(/** @param {any} i */ (i) => Number(i.variant_id) === parentVariantId && isParentLine(i));

      if (!parentItem) {
        console.error('[Secure Shield] Parent line could not be resolved in live cart');
        this.#revertCheckbox(false);
        return;
      }

      const parentQty = parentItem.quantity || 1;

      const shieldItems = liveItems.filter(/** @param {any} i */ (i) =>
        i.parent_relationship && i.parent_relationship.parent_key === parentItem.key
      );

      /** @type {any} */
      let parsedResponse;

      if (shieldItems.length > 0) {
        if (shieldItems.length === 1 && shieldItems[0].quantity === parentQty) {
          return;
        }

        /** @type {Record<string, number>} */
        const updates = {};
        shieldItems.forEach((/** @type {{ key: string }} */ child, /** @type {number} */ idx) => {
          updates[child.key] = idx === 0 ? parentQty : 0;
        });

        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            sections: this.#sectionId,
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) return;
        parsedResponse = await response.json();
      } else {
        const shieldVariantId = Number(this.#shieldVariant);
        if (Number.isNaN(shieldVariantId) || shieldVariantId < 1) {
          console.error('[Secure Shield] Invalid shield variant id');
          this.#revertCheckbox(false);
          return;
        }

        const items = [
          {
            id: this.#shieldVariant,
            quantity: parentQty,
            // Use the freshly-fetched live parent key, NOT this.#parentKey (which
            // reflects data-parent-key rendered by Liquid and goes stale whenever
            // a CartTransform/auto-discount rehashes line keys).
            parent_line_key: parentItem.key,
            properties: {
              _secure_shield_for: parentItem.key,
              _product_type: 'secure_shield',
            },
          },
        ];

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            items,
            sections: this.#sectionId,
            sections_url: window.location.pathname,
          }),
        });

        if (!response.ok) {
          console.error('[Secure Shield] /cart/add.js failed', response.status);
          this.#revertCheckbox(false);
          return;
        }

        parsedResponse = await response.json();

        // Guard 1: Empty items response — server accepted the request but the
        // parent reference did not resolve (silent 200 documented by Shopify support).
        if (!parsedResponse || !Array.isArray(parsedResponse.items) || parsedResponse.items.length === 0) {
          console.error('[Secure Shield] Cart add returned empty items; parent reference did not resolve server-side');
          this.#revertCheckbox(false);
          // fetch the cart again and revert back if already added by Shopify as separate line item
          const cart = await fetch('/cart.js').then(r => r.json());
          const lineItem = cart.items.find(/** @param {any} item */ (item) => (item.properties._product_type == "secure_shield" && !item.parent_relationship));
          if (lineItem) {
            // remove the line item from the cart
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ id: lineItem.key, quantity: 0 }),
            });
          }
          return;
        }

        // Guard 2: Child line was created but is orphaned (no parent_relationship).
        // Roll it back so we don't leave a dangling warranty SKU in the cart.
        const addedChild = parsedResponse.items.find(
          /** @param {any} i */ (i) => Number(i.variant_id) === shieldVariantId
        );
        const childHasParentLink =
          addedChild &&
          addedChild.parent_relationship &&
          (addedChild.parent_relationship.parent_id || addedChild.parent_relationship.parent_key);

        if (!childHasParentLink) {
          console.error('[Secure Shield] Cart add accepted child line but did not establish parent_relationship; rolling back');
          if (addedChild && addedChild.key) {
            try {
              await fetch('/cart/change.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ id: addedChild.key, quantity: 0 }),
              });
            } catch (cleanupError) {
              console.error('[Secure Shield] Failed to clean up orphaned child line:', cleanupError);
            }
          }
          this.#revertCheckbox(false);
          return;
        }

        // Guard 3: The parent's key has almost certainly rotated now that a child
        // line was attached. Re-resolve it from /cart.js before any follow-on
        // /cart/change.js call, otherwise we hit the same stale-key failure mode.
        const postAddCart = await fetch('/cart.js').then(r => r.json());
        const postAddItems = Array.isArray(postAddCart.items) ? postAddCart.items : [];
        const freshParent =
          postAddItems.find(/** @param {any} i */ (i) =>
            Number(i.variant_id) === parentVariantId && isParentLine(i)
          ) || parentItem;

        const parentProperties = {
          ...(freshParent.properties || {}),
          _has_secureshield: true,
          _secureshield_id: shieldVariantId,
        };

        const parentChangeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: freshParent.key,
            quantity: freshParent.quantity || parentQty,
            properties: parentProperties,
            sections: this.#sectionId,
            sections_url: window.location.pathname,
          }),
        });

        if (!parentChangeResponse.ok) {
          console.error('[Secure Shield] Failed to set parent line secure shield properties');
          this.#revertCheckbox(false);
          return;
        }

        parsedResponse = await parentChangeResponse.json();
      }

      this.#updateSectionAndDispatch(parsedResponse);
    } catch (error) {
      console.error('[Secure Shield] Error adding shield:', error);
      this.#revertCheckbox(false);
    } finally {
      this.#busy = false;
      this.#setCheckboxDisabled(false);
      this.#setCartItemsLoading(false);
    }
  }

  // -------------------------------------------------------------------------
  // Cart API — Remove shield (checkbox unchecked)
  // -------------------------------------------------------------------------

  async #removeSecureShield() {
    if (this.#busy) return;
    this.#busy = true;
    this.#setCheckboxDisabled(true);
    this.#setCartItemsLoading(true);

    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      
      
      const parentItem = this.closest(".bv-cart-item");
      const lineItem = cart.items.find(item => item.variant_id == parentItem.dataset.variantId);
      
      // const parentItem = cart.items.find(/** @param {any} i */ (i) => i.key === this.#parentKey);
      const shieldItems = cart.items.filter(/** @param {any} i */ (i) =>
        i.parent_relationship && i.parent_relationship.parent_key === parentItem.dataset.key
      );

      // Step 1: Remove shield child line items
      if (shieldItems.length > 0) {
        /** @type {Record<string, number>} */
        const updates = {};
        shieldItems.forEach((/** @type {{ key: string }} */ child) => {
          updates[child.key] = 0;
        });

        const sectionsParam = parentItem
          ? undefined
          : this.#sectionId;

        const removeResponse = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates,
            ...(sectionsParam ? { sections: sectionsParam, sections_url: window.location.pathname } : {}),
          }),
        });

        if (!removeResponse.ok) {
          this.#revertCheckbox(true);
          return;
        }

        if (!parentItem) {
          const parsedRemove = await removeResponse.json();
          this.#updateSectionAndDispatch(parsedRemove);
        }
      }

      // Step 2: Clear _has_secureshield / _secureshield_id from the parent item
      if (parentItem) {
        const cleanedProperties = { ...(parentItem.properties || {}) };
        cleanedProperties._has_secureshield = false;
        cleanedProperties._secureshield_id = null;

        const changeResponse = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: this.#parentKey,
            quantity: lineItem.quantity,
            properties: cleanedProperties,
            sections: this.#sectionId,
            sections_url: window.location.pathname,
          }),
        });

        if (!changeResponse.ok) {
          this.#revertCheckbox(true);
          return;
        }

        const parsedResponse = await changeResponse.json();
        this.#updateSectionAndDispatch(parsedResponse);
      }
    } catch (error) {
      console.error('[Secure Shield] Error removing shield:', error);
      this.#revertCheckbox(true);
    } finally {
      this.#busy = false;
      this.#setCheckboxDisabled(false);
      this.#setCartItemsLoading(false);
    }
  }

  // -------------------------------------------------------------------------
  // Cart API — Sync shield quantity with parent
  // -------------------------------------------------------------------------

  #debouncedSyncShieldQuantity() {
    if (this.#syncTimeout) clearTimeout(this.#syncTimeout);
    this.#syncTimeout = setTimeout(() => this.#syncShieldQuantity(), 300);
  }

  async #syncShieldQuantity() {
    if (this.#busy) return;
    if (!(this.checkbox instanceof HTMLInputElement) || !this.checkbox.checked) return;

    try {
      const cart = await fetch('/cart.js').then(r => r.json());

      const parentItem = cart.items.find(/** @param {any} i */ (i) => i.key === this.#parentKey);
      if (!parentItem) return;

      const shieldItems = cart.items.filter(/** @param {any} i */ (i) =>
        i.parent_relationship && i.parent_relationship.parent_key === this.#parentKey
      );
      if (shieldItems.length === 0) return;

      const shieldItem = shieldItems[0];
      if (shieldItem.quantity === parentItem.quantity) return;

      this.#busy = true;
      this.#setCheckboxDisabled(true);
      this.#setCartItemsLoading(true);

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: shieldItem.key,
          quantity: parentItem.quantity,
          sections: this.#sectionId,
          sections_url: window.location.pathname,
        }),
      });

      if (!response.ok) return;

      const parsedResponse = await response.json();
      this.#updateSectionAndDispatch(parsedResponse);
    } catch (error) {
      console.error('[Secure Shield] Error syncing shield quantity:', error);
    } finally {
      this.#busy = false;
      this.#setCheckboxDisabled(false);
      this.#setCartItemsLoading(false);
    }
  }

  // -------------------------------------------------------------------------
  // Section morph + event dispatch
  // -------------------------------------------------------------------------

  /** @param {any} parsedResponse */
  #updateSectionAndDispatch(parsedResponse) {
    const sections = parsedResponse.sections || null;
    const sectionHtml = sections && this.#sectionId ? sections[this.#sectionId] : null;

    if (sectionHtml) {
      if (this.#morphSection) {
        try {
          this.#morphSection(this.#sectionId, sectionHtml);
        } catch (err) {
          const el = document.getElementById('shopify-section-' + this.#sectionId);
          if (el) el.innerHTML = sectionHtml;
          window.refreshProtectPlusWidgets?.();
        }
      } else {
        const el = document.getElementById('shopify-section-' + this.#sectionId);
        if (el) el.innerHTML = sectionHtml;
        window.refreshProtectPlusWidgets?.();
      }
    }

    let itemCount = parsedResponse.item_count || 0;
    if (!itemCount && sectionHtml) {
      try {
        const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const countEl = doc.querySelector('[ref="cartItemCount"]');
        if (countEl && countEl.textContent) {
          itemCount = parseInt(countEl.textContent, 10) || 0;
        }
      } catch (_) { /* ignore */ }
    }

    const eventData = {
      itemCount,
      source: 'secure-shield',
      sections,
    };

    if (this.#CartUpdateEvent) {
      document.dispatchEvent(
        // @ts-ignore – Horizon custom event constructor
        new this.#CartUpdateEvent(parsedResponse, this.#sectionId, eventData)
      );
    } else if (this.#ThemeEvents && this.#ThemeEvents.cartUpdate) {
      document.dispatchEvent(
        new CustomEvent(this.#ThemeEvents.cartUpdate, {
          detail: { resource: parsedResponse, sourceId: this.#sectionId, data: eventData },
        })
      );
    } else {
      document.dispatchEvent(
        new CustomEvent('cart:update', {
          detail: { resource: parsedResponse, sourceId: this.#sectionId, data: eventData },
        })
      );
    }
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /** @param {boolean} disabled */
  #setCheckboxDisabled(disabled) {
    if (this.checkbox instanceof HTMLInputElement) {
      this.checkbox.disabled = disabled;
    }
  }

  /**
   * Same overlay + spinner as cart line updates (`component-cart-items` → `cart-items-disabled` in cart-products.liquid).
   * @param {boolean} loading
   */
  #setCartItemsLoading(loading) {
    if (!this.#isCartContext) return;
    const cartItems = this.closest('cart-items-component');
    if (cartItems instanceof HTMLElement) {
      cartItems.classList.toggle('cart-items-disabled', loading);
    }
  }

  /** @param {boolean} state */
  #revertCheckbox(state) {
    if (this.checkbox instanceof HTMLInputElement) {
      this.checkbox.checked = state;
    }
  }
}

customElements.define('secure-shield-component', SecureShieldComponent);
